import * as t from 'io-ts';
import _, {compact} from 'lodash';

import {Dictionary} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {
  makeApiType,
  makeEnumRuntimeType,
  RAccountIdRef,
  RCardGroupIdRef,
  RCardIdRef,
  RContactUidRef,
} from './apiTypes';
import {parseCardGroup, RCardGroups} from './cardGroupModel';
import {buildContactUidFromContactBlob, parseContactInCard, RContacts} from './contactModel';
import {CrmSyncSourcesEnum} from './crmSyncModel';
import {parseCustomFieldAttribute, RCustomFieldAttribute} from './customFieldAttributeModel';

/*
 * Runtime type.
 */

const RLink = t.readonly(
  t.type({
    url: t.string,
  }),
);

export interface Link extends t.TypeOf<typeof RLink> {}

export enum CardTypesEnum {
  AUTO = 'auto',
  ENHANCED = 'enhanced',
}

const RCardExternalInfo = t.type({
  id: t.string,
  source: makeEnumRuntimeType(CrmSyncSourcesEnum),
});

export interface CardExternalInfo extends t.TypeOf<typeof RCardExternalInfo> {}

const RCardDirty = t.readonly(
  t.type({
    id: t.Integer,
    url: t.string,
    namespace: t.string,
    name: t.union([t.string, t.undefined]),
    description: t.union([t.string, t.undefined]),
    links: t.readonlyArray(RLink),
    wasNameEdited: t.boolean,
    bio: t.union([t.string, t.undefined]),
    avatarUrl: t.union([t.string, t.undefined]),
    accountId: t.union([RAccountIdRef, t.undefined]),
    // Deprecated: use numberOfNotesViaNamespaces
    numberOfNotes: t.Integer,
    numberOfNotesViaNamespaces: t.Integer,
    groupIds: t.readonlyArray(RCardGroupIdRef),
    contactUids: t.readonlyArray(RContactUidRef),
    customFieldAttributes: t.union([t.readonlyArray(RCustomFieldAttribute), t.undefined]),
    updatedAt: t.Integer,
    type: makeEnumRuntimeType(CardTypesEnum),
    externalInfo: t.union([RCardExternalInfo, t.undefined]),
  }),
  'card',
);

/*
 * Type.
 */

export interface CardModel extends t.TypeOf<typeof RCardDirty> {}
export const RCard = t.clean<CardModel>(RCardDirty as any);
export const RCards = t.readonlyArray(RCard);
export const RCardsFlat = t.type({
  cards: RCards,
  contacts: RContacts,
  cardGroups: RCardGroups,
});
RCardIdRef.setTargetType(RCard);

/*
 * IO methods.
 */

export function parseCard(src: any): CardModel {
  return {
    id: src.id,
    url: src.url,
    namespace: src.namespace,
    name: (src.name && src.name.trim()) || undefined,
    description: src.description || undefined,
    links: src.links || [],
    wasNameEdited: src.edited,
    bio: src.bio || undefined,
    avatarUrl: src.avatar || undefined,
    accountId: src.account_id || undefined,
    numberOfNotes: src.num_notes,
    numberOfNotesViaNamespaces: parseNamespaceToNumberOfNotes(src.namespace_to_num_notes ?? {}),
    groupIds: src.groups && src.groups.map((g: any) => g.id),
    contactUids: (src.contacts && src.contacts.map(buildContactUidFromContactBlob)) || [],
    customFieldAttributes:
      (src.custom_field_attributes && src.custom_field_attributes.map(parseCustomFieldAttribute)) ||
      undefined,
    updatedAt: src.updated_at || 0,
    type: src.type,
    externalInfo: src.external_info || undefined,
  };
}

function parseNamespaceToNumberOfNotes(src: Dictionary<number>): number {
  return _(src)
    .values()
    .reduce((total, namespaceNoteCount) => {
      if (!namespaceNoteCount) {
        return total;
      }
      return total + namespaceNoteCount;
    }, 0);
}

export function parseCards(src: any) {
  return src.cards && src.cards.map(parseCard);
}

export function parseCardsFlat(src: any) {
  return {
    cards: src && src.map(parseCard),
    contacts:
      src &&
      _(src)
        .map((card: any) => card.contacts.map((contact: any) => parseContactInCard(card, contact)))
        .flatten()
        .value(),
    cardGroups:
      src &&
      _(src)
        .map((card: any) => card.groups.map((cardGroup: any) => parseCardGroup(cardGroup)))
        .flatten()
        .value(),
  };
}

export function parseCardsFromRecipient(recipient: any): ReadonlyArray<CardModel> {
  // Parse the main card, if any.
  const parsedCard = recipient.card && parseCard(recipient.card);

  // Parse the fallback card, if any.
  const fallbackCard = recipient.fallback && recipient.fallback.card;
  const parsedFallbackCard = fallbackCard && parseCard(fallbackCard);

  // Return what we found.
  return compact([parsedCard, parsedFallbackCard]);
}

export function parseCardsFromConversation(src: any): ReadonlyArray<CardModel> {
  return _(src.trackers)
    .map((tracker) => tracker && tracker.recipient)
    .concat(src.contact)
    .compact()
    .map((recipient) => parseCardsFromRecipient(recipient))
    .flatten()
    .uniqBy((card) => card.id)
    .value();
}

function parsePreviewCards(src: any): ReadonlyArray<CardModel> {
  return src.previews.map((cardSrc: any) => parseCard(cardSrc));
}

/*
 * API type.
 */

export const ApiCard = makeApiType('card', RCard, parseCard);
export const ApiCards = makeApiType('cards', RCards, parseCards);
export const ApiPreviewCards = makeApiType('cards', RCards, parsePreviewCards);
export const ApiCardsFlat = makeApiType('cardsFlat', RCardsFlat, parseCardsFlat);
